import React, { useState, useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import API from '../../utils/API';
import Toast from '../../components/Toast/Toast';
import TrialCredentialsModal from '../../components/Modals/TrialCredentialsModal';
import './TrialProvisioning.css';

const TrialProvisioning = () => {
  const intl = useIntl();
  const [formData, setFormData] = useState({
    accountOwnerEmail: '',
    firstName1: '',
    lastName1: '',
    email1: '',
    firstName2: '',
    lastName2: '',
    email2: '',
    orgName: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentialsData, setCredentialsData] = useState(null);
  const [validatingOwner, setValidatingOwner] = useState(false);
  const [ownerValidated, setOwnerValidated] = useState(false);

  // Helper function to trim all string values in an object
  const trimFormData = useCallback((data) => {
    return Object.entries(data).reduce((acc, [key, value]) => {
      acc[key] = typeof value === 'string' ? value.trim() : value;
      return acc;
    }, {});
  }, []);

  // Load last used account owner email on component mount
  useEffect(() => {
    const lastAccountOwnerEmail = localStorage.getItem('trialProvisioningAccountOwner');
    if (lastAccountOwnerEmail) {
      setFormData(prev => ({
        ...prev,
        accountOwnerEmail: lastAccountOwnerEmail
      }));
      // Auto-validate the stored email
      validateAccountOwner(lastAccountOwnerEmail);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate account owner email
  const validateAccountOwner = async (email) => {
    if (!email || !email.trim()) {
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setValidationErrors(prev => ({
        ...prev,
        accountOwnerEmail: intl.formatMessage({ id: 'INVALID_EMAIL_FORMAT' })
      }));
      setOwnerValidated(false);
      return;
    }

    setValidatingOwner(true);
    try {
      const response = await API.validateAccountOwner(email);
      if (response.data && response.data.isValid) {
        setOwnerValidated(true);
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.accountOwnerEmail;
          return newErrors;
        });
        // Save successfully validated email to localStorage
        localStorage.setItem('trialProvisioningAccountOwner', email.trim());
      } else {
        setOwnerValidated(false);
        setValidationErrors(prev => ({
          ...prev,
          accountOwnerEmail: 'User not found or is not an account owner'
        }));
      }
    } catch (error) {
      setOwnerValidated(false);
      setValidationErrors(prev => ({
        ...prev,
        accountOwnerEmail: 'Failed to validate account owner email'
      }));
    } finally {
      setValidatingOwner(false);
    }
  };

  // Validate form based on operation type
  const validateForm = (operation) => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Account owner email is always required
    if (!formData.accountOwnerEmail.trim()) {
      errors.accountOwnerEmail = intl.formatMessage({ id: 'ACCOUNT_OWNER_EMAIL_REQUIRED' }) || 'Account owner email is required';
    } else if (!emailRegex.test(formData.accountOwnerEmail)) {
      errors.accountOwnerEmail = intl.formatMessage({ id: 'INVALID_EMAIL_FORMAT' });
    } else if (!ownerValidated && validationErrors.accountOwnerEmail) {
      // Only show validation error if we've already tried to validate and it failed
      errors.accountOwnerEmail = validationErrors.accountOwnerEmail;
    }

    // Email1 and orgName are always required for both operations
    if (!formData.email1.trim()) {
      errors.email1 = intl.formatMessage({ id: 'EMAIL_1_REQUIRED' });
    } else if (!emailRegex.test(formData.email1)) {
      errors.email1 = intl.formatMessage({ id: 'INVALID_EMAIL_FORMAT' });
    }
    
    if (!formData.orgName.trim()) {
      errors.orgName = intl.formatMessage({ id: 'ORGANIZATION_NAME_REQUIRED' });
    }

    if (operation === 'provision') {
      // For provision: firstName1 and lastName1 are required for first user
      if (!formData.firstName1.trim()) errors.firstName1 = intl.formatMessage({ id: 'FIRST_NAME_REQUIRED' });
      if (!formData.lastName1.trim()) errors.lastName1 = intl.formatMessage({ id: 'LAST_NAME_REQUIRED' });
      
      // Email2 is optional for provision
      // If email2 is provided but names are not, backend will use User 1's names
      if (formData.email2.trim()) {
        if (!emailRegex.test(formData.email2)) {
          errors.email2 = intl.formatMessage({ id: 'INVALID_EMAIL_FORMAT' });
        }
        
        if (formData.email1.trim() && formData.email1 === formData.email2) {
          errors.email2 = intl.formatMessage({ id: 'EMAIL_ADDRESSES_MUST_BE_DIFFERENT' });
        }
        
        // firstName2 and lastName2 are optional - they will default to User 1's names if not provided
      }
    } else if (operation === 'unprovision') {
      // For unprovision: email2 is optional (matches provision behavior)
      // Only validate if provided
      if (formData.email2 && formData.email2.trim()) {
        if (!emailRegex.test(formData.email2.trim())) {
          errors.email2 = intl.formatMessage({ id: 'INVALID_EMAIL_FORMAT' });
        }
        
        // Check that emails are different if both provided
        if (formData.email1.trim() && formData.email1.trim() === formData.email2.trim()) {
          errors.email2 = intl.formatMessage({ id: 'EMAIL_ADDRESSES_MUST_BE_DIFFERENT' });
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form change
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    
    // Reset owner validation if account owner email changes
    if (name === 'accountOwnerEmail') {
      setOwnerValidated(false);
    }
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Handle blur on account owner email to auto-validate
  const handleAccountOwnerBlur = () => {
    if (formData.accountOwnerEmail && formData.accountOwnerEmail.trim()) {
      validateAccountOwner(formData.accountOwnerEmail.trim());
    }
  };

  // Handle credentials modal close
  const handleCredentialsModalClose = () => {
    setShowCredentialsModal(false);
    setCredentialsData(null);
    setSuccess(intl.formatMessage({ id: 'TRIAL_ACCOUNTS_CREATED_SUCCESS' }));
  };

  // Handle provision submission using the new backend route
  const handleProvision = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    
    // Auto-validate account owner if not already validated
    if (formData.accountOwnerEmail && !ownerValidated) {
      setValidatingOwner(true);
      const trimmedEmail = formData.accountOwnerEmail.trim();
      try {
        const response = await API.validateAccountOwner(trimmedEmail);
        if (!response.data || !response.data.isValid) {
          setValidationErrors(prev => ({
            ...prev,
            accountOwnerEmail: 'User not found or is not an account owner'
          }));
          setOwnerValidated(false);
          setValidatingOwner(false);
          return;
        }
        setOwnerValidated(true);
        // Save successfully validated email to localStorage
        localStorage.setItem('trialProvisioningAccountOwner', trimmedEmail);
      } catch (error) {
        setValidationErrors(prev => ({
          ...prev,
          accountOwnerEmail: 'Failed to validate account owner email'
        }));
        setOwnerValidated(false);
        setValidatingOwner(false);
        return;
      }
      setValidatingOwner(false);
    }
    
    if (!validateForm('provision')) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Call the backend endpoint with trimmed values
      const trimmedData = trimFormData(formData);
      const response = await API.provisionTrialAccounts(trimmedData);

      // Transform backend response to match original modal structure
      const backendData = response.data.data;
      const transformedCredentials = {
        account1: {
          userId: backendData.account1.userId,
          email: backendData.account1.email,
          password: backendData.account1.password
        },
        createdGroups: backendData.createdGroups, // Will be populated in future steps
        orgName: backendData.orgName
      };

      // Only include account2 if it was created
      if (backendData.account2) {
        transformedCredentials.account2 = {
          userId: backendData.account2.userId,
          email: backendData.account2.email,
          password: backendData.account2.password
        };
      }
      
      // Store credentials data and show modal
      setCredentialsData(transformedCredentials);
      setShowCredentialsModal(true);
      
      // Reset form but keep account owner email
      setFormData(prev => ({
        accountOwnerEmail: prev.accountOwnerEmail,
        firstName1: '',
        lastName1: '',
        email1: '',
        firstName2: '',
        lastName2: '',
        email2: '',
        orgName: '',
      }));
      setSubmitted(false);
      // Keep ownerValidated as true since we're keeping the email
      
    } catch (error) {
      // Provide more specific error messages
      let errorMessage = 'Failed to create trial accounts. ';
      if (error.response?.status === 400) {
        // Use server-provided message if available
        const serverMessage = error.response?.data;
        if (typeof serverMessage === 'string') {
          errorMessage = serverMessage;
        } else {
        errorMessage += 'Invalid input data. Please check your entries.';
        }
      } else if (error.response?.status === 403) {
        errorMessage += 'Insufficient permissions. Support role required.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error occurred. Please try again later.';
      } else {
        errorMessage += `Error: ${error.message || 'Unknown error occurred'}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle unprovision submission using the new backend route
  const handleUnprovision = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    
    // Auto-validate account owner if not already validated
    if (formData.accountOwnerEmail && !ownerValidated) {
      setValidatingOwner(true);
      const trimmedEmail = formData.accountOwnerEmail.trim();
      try {
        const response = await API.validateAccountOwner(trimmedEmail);
        if (!response.data || !response.data.isValid) {
          setValidationErrors(prev => ({
            ...prev,
            accountOwnerEmail: 'User not found or is not an account owner'
          }));
          setOwnerValidated(false);
          setValidatingOwner(false);
          return;
        }
        setOwnerValidated(true);
        // Save successfully validated email to localStorage
        localStorage.setItem('trialProvisioningAccountOwner', trimmedEmail);
      } catch (error) {
        setValidationErrors(prev => ({
          ...prev,
          accountOwnerEmail: 'Failed to validate account owner email'
        }));
        setOwnerValidated(false);
        setValidatingOwner(false);
        return;
      }
      setValidatingOwner(false);
    }
    
    if (!validateForm('unprovision')) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Call the backend endpoint with trimmed values
      const trimmedData = trimFormData(formData);
      const response = await API.unprovisionTrialAccounts({
        accountOwnerEmail: trimmedData.accountOwnerEmail,
        email1: trimmedData.email1 || undefined,
        email2: trimmedData.email2 || undefined,
        orgName: trimmedData.orgName || undefined
      });

      // Extract results from backend response
      const unprovisionData = response.data.data;
      
      // Check for "no users found" response from backend
      if (response.data.message && response.data.message.includes('No matching users found')) {
        setError('No matching trial accounts found for the provided criteria. Please verify the email addresses and organization name.');
        return;
      }
      
      // Check if any users were actually found and deleted
      if (!unprovisionData.usersDeleted || unprovisionData.usersDeleted.length === 0) {
        // No users found - this should be treated as an informational message, not success
        setError('No matching trial accounts found for the provided criteria. Please verify the email addresses and organization name.');
        return;
      }

      // Show detailed success message for actual deletions
      let successMessage = `Successfully unprovisioned trial accounts.`;
      
      if (unprovisionData.usersDeleted.length > 0) {
        successMessage += ` Users deleted: ${unprovisionData.usersDeleted.join(', ')}.`;
      }
      
      if (unprovisionData.groupsDeleted.length > 0) {
        successMessage += ` Groups deleted: ${unprovisionData.groupsDeleted.join(', ')}.`;
      }

      setSuccess(successMessage);
      
      // Reset form but keep account owner email
      setFormData(prev => ({
        accountOwnerEmail: prev.accountOwnerEmail,
        firstName1: '',
        lastName1: '',
        email1: '',
        firstName2: '',
        lastName2: '',
        email2: '',
        orgName: '',
      }));
      setSubmitted(false);
      // Keep ownerValidated as true since we're keeping the email
      
    } catch (error) {
      // Provide more specific error messages
      let errorMessage = 'Failed to unprovision trial accounts. ';
      if (error.response?.status === 400) {
        // Use server-provided message if available
        const serverMessage = error.response?.data;
        if (typeof serverMessage === 'string') {
          errorMessage = serverMessage;
        } else {
          errorMessage += 'Invalid criteria provided. Please check your entries.';
        }
      } else if (error.response?.status === 403) {
        errorMessage += 'Insufficient permissions. Support role required.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error occurred. Please try again later.';
      } else {
        errorMessage += `Error: ${error.message || 'Unknown error occurred'}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="title">
        <FormattedMessage id="TRIAL_PROVISIONING" defaultMessage="Trial Provisioning" />
      </div>
      <div className="card" style={{ padding: '16px'}}>
        <div className="trialProvisioningTitle pb-4 fs-6 fw-bold">
          <FormattedMessage id="ENTER_USER_INFORMATION" defaultMessage="Enter User Information" />
        </div>
        
        <form className="trial-provisioning-form">
          {/* Account Owner Section */}
          <div className="account-owner-section">
            <div className="form-group">
              <label htmlFor="accountOwnerEmail" className="col-form-label">
                <FormattedMessage id="ACCOUNT_OWNER_EMAIL" defaultMessage="Account Owner Email" />
                <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span>
              </label>
              <div className="position-relative">
                <input
                  type="email"
                  id="accountOwnerEmail"
                  name="accountOwnerEmail"
                  value={formData.accountOwnerEmail}
                  onChange={handleChange}
                  onBlur={handleAccountOwnerBlur}
                  className={`form-control ${submitted && validationErrors.accountOwnerEmail ? 'is-invalid' : ''} ${ownerValidated ? 'is-valid' : ''}`}
                  disabled={loading || validatingOwner}
                  placeholder={intl.formatMessage({ id: "ACCOUNT_OWNER_EMAIL_PLACEHOLDER", defaultMessage: "accountowner@organization.com" })}
                />
                {validatingOwner && (
                  <span className="spinner-border spinner-border-sm position-absolute" style={{ right: '12px', top: '12px' }} role="status"></span>
                )}
                {submitted && validationErrors.accountOwnerEmail && (
                  <div className="invalid-feedback">{validationErrors.accountOwnerEmail}</div>
                )}
                {ownerValidated && (
                  <div className="valid-feedback">Account owner verified (saved for future use)</div>
                )}
              </div>
              <small className="form-text text-muted">
                <FormattedMessage id="ACCOUNT_OWNER_EMAIL_HINT" defaultMessage="The trial users will be associated with this account owner's account. Your last entry will be remembered." />
              </small>
            </div>
          </div>

          {/* User Sections Container */}
          <div className="users-container">
            {/* User 1 Fields */}
            <div className="user-section">
              <div className="user-section-header">
                <h6 className="user-section-title">
                  <FormattedMessage id="USER_1_INFORMATION" defaultMessage="User #1 Information" />
                </h6>
              </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName1" className="col-form-label">
                <FormattedMessage id="FIRST_NAME" defaultMessage="First Name" />
              </label>
              <input
                type="text"
                id="firstName1"
                name="firstName1"
                value={formData.firstName1}
                onChange={handleChange}
                className={`form-control ${submitted && validationErrors.firstName1 ? 'is-invalid' : ''}`}
                placeholder={intl.formatMessage({ id: "JOHN", defaultMessage: "John" })}
                disabled={loading}
              />
              {submitted && validationErrors.firstName1 && (
                <div className="invalid-feedback">{validationErrors.firstName1}</div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName1" className="col-form-label">
                <FormattedMessage id="LAST_NAME" defaultMessage="Last Name" />
              </label>
              <input
                type="text"
                id="lastName1"
                name="lastName1"
                value={formData.lastName1}
                onChange={handleChange}
                className={`form-control ${submitted && validationErrors.lastName1 ? 'is-invalid' : ''}`}
                placeholder={intl.formatMessage({ id: "DOE", defaultMessage: "Doe" })}
                disabled={loading}
              />
              {submitted && validationErrors.lastName1 && (
                <div className="invalid-feedback">{validationErrors.lastName1}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email1" className="col-form-label">
                <FormattedMessage id="EMAIL_ADDRESS" defaultMessage="Email Address" />
              </label>
              <input
                type="email"
                id="email1"
                name="email1"
                value={formData.email1}
                onChange={handleChange}
                className={`form-control ${submitted && validationErrors.email1 ? 'is-invalid' : ''}`}
                disabled={loading}
                placeholder={intl.formatMessage({ id: "USER_1_EMAIL", defaultMessage: "user1@organization.com" })}
              />
              {submitted && validationErrors.email1 && (
                <div className="invalid-feedback">{validationErrors.email1}</div>
              )}
              <small className="form-text text-muted">
                <FormattedMessage id="TRIAL_ACCOUNT_1_ROLES" defaultMessage="Account #1: SBMM Front Desker, Signage Plus Owner, Alert Plus Safety Officer" />
              </small>
            </div>
          </div>
          </div>

          {/* User 2 Fields */}
          <div className="user-section">
            <div className="user-section-header">
              <h6 className="user-section-title">
                <FormattedMessage id="USER_2_INFORMATION" defaultMessage="User #2 Information" /> <span style={{color: '#6c757d', fontSize: '0.9em'}}>(optional)</span>
              </h6>
            </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName2" className="col-form-label">
                <FormattedMessage id="FIRST_NAME" defaultMessage="First Name" />
              </label>
              <input
                type="text"
                id="firstName2"
                name="firstName2"
                value={formData.firstName2}
                onChange={handleChange}
                className={`form-control ${submitted && validationErrors.firstName2 ? 'is-invalid' : ''}`}
                placeholder={intl.formatMessage({ id: "JANE", defaultMessage: "Jane" })}
                disabled={loading}
              />
              {submitted && validationErrors.firstName2 && (
                <div className="invalid-feedback">{validationErrors.firstName2}</div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName2" className="col-form-label">
                <FormattedMessage id="LAST_NAME" defaultMessage="Last Name" />
              </label>
              <input
                type="text"
                id="lastName2"
                name="lastName2"
                value={formData.lastName2}
                onChange={handleChange}
                className={`form-control ${submitted && validationErrors.lastName2 ? 'is-invalid' : ''}`}
                placeholder={intl.formatMessage({ id: "SMITH", defaultMessage: "Smith" })}
                disabled={loading}
              />
              {submitted && validationErrors.lastName2 && (
                <div className="invalid-feedback">{validationErrors.lastName2}</div>
              )}
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email2" className="col-form-label">
                <FormattedMessage id="EMAIL_ADDRESS" defaultMessage="Email Address" />
              </label>
              <input
                type="email"
                id="email2"
                name="email2"
                value={formData.email2}
                onChange={handleChange}
                className={`form-control ${submitted && validationErrors.email2 ? 'is-invalid' : ''}`}
                disabled={loading}
                placeholder={intl.formatMessage({ id: "USER_2_EMAIL", defaultMessage: "user2@organization.com" })}
              />
              {submitted && validationErrors.email2 && (
                <div className="invalid-feedback">{validationErrors.email2}</div>
              )}
              <small className="form-text text-muted">
                <FormattedMessage id="TRIAL_ACCOUNT_2_ROLES" defaultMessage="Account #2: Alert Plus Teacher" />
              </small>
            </div>
          </div>
          </div>
          </div>

          {/* Organization Section */}
          <div className="organization-section">
            <div className="form-group">
              <label htmlFor="orgName" className="col-form-label">
                <FormattedMessage id="ORGANIZATION_NAME" defaultMessage="Organization Name" />
              </label>
              <input
                type="text"
                id="orgName"
                name="orgName"
                value={formData.orgName}
                onChange={handleChange}
                className={`form-control ${submitted && validationErrors.orgName ? 'is-invalid' : ''}`}
                disabled={loading}
                placeholder={intl.formatMessage({ id: "ORGANIZATION_NAME_HINT", defaultMessage: "Organization Name for Groups" })}
              />
              {submitted && validationErrors.orgName && (
                <div className="invalid-feedback">{validationErrors.orgName}</div>
              )}
              <small className="form-text text-muted">
                <FormattedMessage id="ORGANIZATION_NAME_HINT" defaultMessage="Groups will be created in SaaS features using this organization name" />
              </small>
            </div>
          </div>

          <div className="button-group">
            <button 
              type="button" 
              className="btn btn-primary me-3" 
              onClick={handleProvision}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  <FormattedMessage id="PROVISIONING" defaultMessage="Provisioning..." />
                </>
              ) : (
                <FormattedMessage id="PROVISION_TRIAL" defaultMessage="Provision Trial" />
              )}
            </button>
            
            <button 
              type="button" 
              className="btn btn-danger me-3" 
              onClick={handleUnprovision}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  <FormattedMessage id="UNPROVISIONING" defaultMessage="Unprovisioning..." />
                </>
              ) : (
                <FormattedMessage id="UNPROVISION_TRIAL" defaultMessage="Unprovision Trial" />
              )}
            </button>
          </div>

          <div className="info-section mt-4">
            <h6><FormattedMessage id="TRIAL_INFO" defaultMessage="Trial Provisioning Information" /></h6>
            <ul className="list-unstyled">
              <li>• <FormattedMessage id="TRIAL_CREATES_ACCOUNTS" defaultMessage="Creates trial accounts with secure passwords" /></li>
              <li>• <FormattedMessage id="TRIAL_ACCOUNT_1_ROLES" defaultMessage="Account #1: SBMM Front Desker, Signage Plus Owner, Alert Plus Safety Officer" /></li>
              <li>• <FormattedMessage id="TRIAL_ACCOUNT_2_ROLES" defaultMessage="Account #2: Alert Plus Teacher (optional)" /></li>
              <li>• <FormattedMessage id="TRIAL_CREATES_GROUPS" defaultMessage="Creates groups and assigns users for testing" /></li>
              <li>• <FormattedMessage id="TRIAL_UNPROVISION_WARNING" defaultMessage="Unprovision removes all trial data" /></li>
            </ul>
          </div>
        </form>
      </div>
      
      <Toast
        success={success}
        error={error}
        setSuccess={setSuccess}
        setError={setError}
      />
      
      {/* Credentials Modal */}
      {credentialsData && (
        <TrialCredentialsModal
          isOpen={showCredentialsModal}
          onClose={handleCredentialsModalClose}
          account1={credentialsData.account1}
          account2={credentialsData.account2}
          createdGroups={credentialsData.createdGroups}
          orgName={credentialsData.orgName}
        />
      )}
    </div>
  );
};

export default TrialProvisioning;